// "Add something" - what the + button opens.
//
// THREE SIZES OF THING, and the sheet exists because they are easy to confuse.
// The approved design puts the definition next to each one at the moment of
// maximum confusion (prototype-v2.md section 1.7): "one thing to do, once" and
// "something that comes back and starts again each day" are the whole
// distinction, and no rename fixes it - "one-off thing" and "reminder" are
// near-synonyms in English, so what separates them is structural. A reminder
// belongs to no schedule; everything else does.
//
// The reminder is FIRST, as it is in the prototype, because it is the smallest
// and the most common thing somebody arrives wanting to write down.

import { Sheet } from './Sheet.tsx';
import { Icon } from '../day/Icon.tsx';

interface Props {
  onNewReminder: () => void;
  onNewItem: () => void;
  onNewSchedule: () => void;
  /**
   * Whether the account has any schedule at all.
   *
   * With none, "Something to do" leads to a picker with nothing in it, so the
   * row is left out rather than offered and then apologised for. A REMINDER is
   * still offered, and that is the point of asking: a reminder belongs to no
   * schedule, so it is the one thing an account with none can still write down.
   */
  hasSchedules: boolean;
  onClose: () => void;
}

export function AddSheet({
  onNewReminder,
  onNewItem,
  onNewSchedule,
  hasSchedules,
  onClose,
}: Props) {
  return (
    <Sheet
      title="Add something"
      onClose={onClose}
      footer={
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
      }
    >
      <h3>Add something</h3>
      <p className="sub">
        {hasSchedules
          ? 'Different sizes of thing live in Perfect Note.'
          : 'You have no schedules yet, so there are two things to choose from.'}
      </p>

      <div className="setlist">
        <button className="setrow" onClick={onNewReminder}>
          <Icon name="pin" />
          <span className="st">
            <b>A reminder</b>
            <span>
              One thing to do, once. It waits here until you do it, and then it is finished for
              good.
            </span>
          </span>
          <span className="chev">
            <Icon name="chevR" />
          </span>
        </button>

        {hasSchedules && (
          <button className="setrow" onClick={onNewItem}>
            <Icon name="circle" />
            <span className="st">
              <b>Something to do</b>
              <span>
                One thing, inside a schedule you already have. It comes back as often as you say.
              </span>
            </span>
            <span className="chev">
              <Icon name="chevR" />
            </span>
          </button>
        )}

        <button className="setrow" onClick={onNewSchedule}>
          <Icon name="today" />
          <span className="st">
            <b>A schedule</b>
            <span>
              A part of your life you want to see on its own. Work, School and Gym are schedules.
            </span>
          </span>
          <span className="chev">
            <Icon name="chevR" />
          </span>
        </button>
      </div>
    </Sheet>
  );
}
